<template>
  <div class="test-plan-list">
    <a-page-header title="测试计划管理 V2" sub-title="基于快照的测试计划执行">
      <template #extra>
        <a-space>
          <a-button type="primary" @click="goToCreatePage">
            <template #icon><PlusOutlined /></template>
            创建测试计划（新）
          </a-button>
          <a-button @click="showCreateDialog">
            <template #icon><PlusOutlined /></template>
            快速创建
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="testPlans"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.execution_status)">
              {{ getStatusText(record.execution_status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'pass_rate'">
            <a-progress
              v-if="record.pass_rate !== null"
              :percent="parseFloat(record.pass_rate)"
              :status="parseFloat(record.pass_rate) >= 80 ? 'success' : 'exception'"
              style="width: 120px"
            />
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewDetail(record.id)">详情</a-button>
              <a-button
                type="primary"
                size="small"
                @click="executePlan(record.id)"
                :disabled="record.execution_status === 'running'"
              >
                {{ record.execution_status === 'running' ? '执行中' : '执行' }}
              </a-button>
              <a-button
                type="link"
                size="small"
                danger
                @click="cancelPlan(record.id)"
                v-if="record.execution_status === 'running'"
              >
                取消
              </a-button>
              <a-popconfirm
                title="确定要删除此测试计划吗？"
                @confirm="deletePlan(record.id)"
                v-if="record.execution_status !== 'running'"
              >
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建测试计划对话框 -->
    <a-modal
      v-model:open="createDialogVisible"
      title="创建测试计划"
      width="800px"
      @ok="handleCreate"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="计划名称" required>
          <a-input v-model:value="createForm.plan_name" placeholder="请输入计划名称" />
        </a-form-item>
        <a-form-item label="测试目的">
          <a-textarea v-model:value="createForm.plan_purpose" :rows="2" />
        </a-form-item>
        
        <a-divider>测试范围</a-divider>
        
        <a-form-item label="测试用例">
          <a-select
            v-model:value="createForm.test_case_ids"
            mode="multiple"
            placeholder="选择测试用例"
            style="width: 100%"
            @dropdownVisibleChange="onTestCaseDropdownChange"
          >
            <a-select-option
              v-for="testCase in testCases"
              :key="testCase.id"
              :value="testCase.id"
            >
              {{ testCase.case_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="快照选择模式">
          <a-select v-model:value="createForm.snapshot_selection_mode">
            <a-select-option value="latest">使用最新快照</a-select-option>
            <a-select-option value="baseline">使用基线快照</a-select-option>
            <a-select-option value="custom">自定义选择</a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="测试轮次">
          <a-input-number
            v-model:value="createForm.test_rounds"
            :min="1"
            :max="10"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 执行进度对话框 -->
    <a-modal
      v-model:open="executionProgressVisible"
      title="测试执行中"
      width="500px"
      :closable="executionProgress.status !== 'running'"
      :maskClosable="false"
      :keyboard="false"
    >
      <div class="execution-progress">
        <!-- 进度条 -->
        <a-progress 
          :percent="executionProgress.progress" 
          :status="progressStatus"
          :strokeColor="progressColor"
        />
        
        <div class="progress-text">
          {{ executionProgress.completed }} / {{ executionProgress.total }} 任务已完成
        </div>
        
        <!-- 统计信息 -->
        <a-row :gutter="16" style="margin-top: 24px">
          <a-col :span="8">
            <a-statistic title="成功" :value="executionProgress.successful" :value-style="{ color: '#52c41a' }">
              <template #prefix>
                <CheckCircleOutlined />
              </template>
            </a-statistic>
          </a-col>
          <a-col :span="8">
            <a-statistic title="失败" :value="executionProgress.failed" :value-style="{ color: '#ff4d4f' }">
              <template #prefix>
                <CloseCircleOutlined />
              </template>
            </a-statistic>
          </a-col>
          <a-col :span="8">
            <a-statistic title="进行中" :value="executionProgress.total - executionProgress.completed">
              <template #prefix>
                <SyncOutlined :spin="executionProgress.status === 'running'" />
              </template>
            </a-statistic>
          </a-col>
        </a-row>
        
        <!-- 完成结果 -->
        <div v-if="executionProgress.status === 'completed'" style="margin-top: 24px">
          <a-result
            :status="executionProgress.failed === 0 ? 'success' : 'warning'"
            :title="executionProgress.failed === 0 ? '全部执行成功！' : '执行完成'"
            :sub-title="`通过率: ${executionProgress.pass_rate || 0}%`"
          />
        </div>
        
        <div v-if="executionProgress.status === 'cancelled'" style="margin-top: 24px">
          <a-result
            status="info"
            title="执行已取消"
            :sub-title="`已完成 ${executionProgress.completed} 个任务`"
          />
        </div>
      </div>
      
      <template #footer>
        <a-button 
          v-if="executionProgress.status === 'running'"
          danger
          @click="handleCancelExecution"
        >
          取消执行
        </a-button>
        <a-button 
          v-if="executionProgress.status !== 'running'"
          type="primary"
          @click="closeProgressDialog"
        >
          关闭
        </a-button>
        <a-button 
          v-if="executionProgress.status === 'completed'"
          @click="viewResults"
        >
          查看结果
        </a-button>
      </template>
    </a-modal>

    <!-- 详情监控对话框 -->
    <a-modal
      v-model:open="executionDialogVisible"
      title="测试计划详情"
      width="900px"
      :footer="null"
    >
      <div v-if="currentExecution">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="计划名称" :span="2">
            {{ currentExecution.plan_name }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(currentExecution.execution_status)">
              {{ getStatusText(currentExecution.execution_status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="通过率">
            {{ currentExecution.pass_rate || 0 }}%
          </a-descriptions-item>
          <a-descriptions-item label="总执行数">
            {{ currentExecution.total_executions || 0 }}
          </a-descriptions-item>
          <a-descriptions-item label="成功/失败">
            {{ currentExecution.successful_executions || 0 }} / {{ currentExecution.failed_executions || 0 }}
          </a-descriptions-item>
        </a-descriptions>

        <a-divider />

        <a-space>
          <a-button type="primary" @click="viewResultsFromDetail">查看详细结果</a-button>
          <a-button @click="triggerAutoScore">触发自动评分</a-button>
        </a-space>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { 
  PlusOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  SyncOutlined 
} from '@ant-design/icons-vue';
import {
  getTestPlans,
  createTestPlan,
  getTestPlanDetail,
  deleteTestPlan as deletePlanAPI,
  executeTestPlan,
  getExecutionStatus,
  cancelExecution
} from '@/api/evalTestPlanV2';
import { getTestCases } from '@/api/evalTestCaseV2';
import { batchAutoScore } from '@/api/evalExecutionV2';

const router = useRouter();

const testPlans = ref([]);
const testCases = ref([]);
const loading = ref(false);
const createDialogVisible = ref(false);
const executionDialogVisible = ref(false);
const executionProgressVisible = ref(false);
const currentExecution = ref(null);

// 轮询定时器
let pollingTimer = null;

// 执行进度状态
const executionProgress = reactive({
  plan_id: null,
  plan_name: '',
  status: 'pending',
  progress: 0,
  total: 0,
  completed: 0,
  successful: 0,
  failed: 0,
  pass_rate: 0
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0
});

const createForm = reactive({
  plan_name: '',
  plan_purpose: '',
  test_case_ids: [],
  snapshot_selection_mode: 'latest',
  test_rounds: 1
});

const columns = [
  { title: '计划编码', dataIndex: 'plan_code', key: 'plan_code', width: 150 },
  { title: '计划名称', dataIndex: 'plan_name', key: 'plan_name', width: 200 },
  { title: '状态', dataIndex: 'execution_status', key: 'status', width: 120 },
  { title: '测试轮次', dataIndex: 'test_rounds', key: 'test_rounds', width: 100 },
  { title: '总执行数', dataIndex: 'total_executions', key: 'total_executions', width: 100 },
  { title: '通过率', dataIndex: 'pass_rate', key: 'pass_rate', width: 150 },
  { title: '开始时间', dataIndex: 'start_time', key: 'start_time', width: 180 },
  { title: '操作', key: 'action', width: 260, fixed: 'right' }
];

// 进度条状态
const progressStatus = computed(() => {
  if (executionProgress.status === 'completed') {
    return executionProgress.failed === 0 ? 'success' : 'exception';
  }
  if (executionProgress.status === 'cancelled') {
    return 'exception';
  }
  return 'active';
});

// 进度条颜色
const progressColor = computed(() => {
  if (executionProgress.status === 'completed' && executionProgress.failed === 0) {
    return '#52c41a';
  }
  if (executionProgress.failed > 0) {
    return '#faad14';
  }
  return '#1890ff';
});

const fetchTestPlans = async () => {
  loading.value = true;
  try {
    const res = await getTestPlans({
      page: pagination.current,
      pageSize: pagination.pageSize
    });
    if (res.code === 200) {
      testPlans.value = res.data?.list || [];
      pagination.total = res.data?.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取测试计划失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const fetchTestCases = async () => {
  try {
    const res = await getTestCases({ status: 'active', pageSize: 100 });
    if (res.code === 200) {
      testCases.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取测试用例失败:', error);
  }
};

const onTestCaseDropdownChange = (open) => {
  if (open && testCases.value.length === 0) {
    fetchTestCases();
  }
};

const showCreateDialog = () => {
  Object.assign(createForm, {
    plan_name: '',
    plan_purpose: '',
    test_case_ids: [],
    snapshot_selection_mode: 'latest',
    test_rounds: 1
  });
  createDialogVisible.value = true;
};

// 跳转到新的创建页面
const goToCreatePage = () => {
  router.push('/eval-v2/test-plans/create');
};

const handleCreate = async () => {
  try {
    await createTestPlan({
      ...createForm,
      created_by: 'admin'
    });
    message.success('创建成功');
    createDialogVisible.value = false;
    fetchTestPlans();
  } catch (error) {
    message.error('创建失败：' + error.message);
  }
};

// 执行测试计划（异步）
const executePlan = async (id) => {
  try {
    const res = await executeTestPlan(id);
    
    if (res.code === 200) {
      message.success('测试计划已启动');
      
      // 初始化进度信息
      Object.assign(executionProgress, {
        plan_id: id,
        plan_name: res.data.plan_name || '',
        status: 'running',
        progress: 0,
        total: res.data.total_tasks || 0,
        completed: 0,
        successful: 0,
        failed: 0,
        pass_rate: 0
      });
      
      // 显示进度对话框
      executionProgressVisible.value = true;
      
      // 启动轮询
      startPolling(id);
      
      // 刷新列表
      fetchTestPlans();
    } else {
      message.error(res.message || '启动执行失败');
    }
  } catch (error) {
    message.error('执行失败：' + error.message);
  }
};

// 启动轮询
const startPolling = (planId) => {
  stopPolling();
  
  // 立即查询一次
  pollStatus(planId);
  
  // 每3秒查询一次
  pollingTimer = setInterval(() => {
    pollStatus(planId);
  }, 3000);
};

// 轮询状态
const pollStatus = async (planId) => {
  try {
    const res = await getExecutionStatus(planId);
    
    if (res.code === 200) {
      const data = res.data;
      
      Object.assign(executionProgress, {
        plan_id: data.plan_id,
        plan_name: data.plan_name,
        status: data.status,
        progress: data.progress,
        total: data.total,
        completed: data.completed,
        successful: data.successful,
        failed: data.failed,
        pass_rate: data.pass_rate
      });
      
      // 如果执行完成或取消，停止轮询
      if (data.status === 'completed' || data.status === 'cancelled' || data.status === 'failed') {
        stopPolling();
        fetchTestPlans();
      }
    }
  } catch (error) {
    console.error('轮询状态失败:', error);
  }
};

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
  }
};

// 取消执行
const handleCancelExecution = async () => {
  try {
    await cancelExecution(executionProgress.plan_id);
    message.info('已发送取消请求');
  } catch (error) {
    message.error('取消失败：' + error.message);
  }
};

// 关闭进度对话框
const closeProgressDialog = () => {
  executionProgressVisible.value = false;
  stopPolling();
};

// 查看结果
const viewResults = () => {
  closeProgressDialog();
  router.push({ path: '/eval-v2/results', query: { test_plan_id: executionProgress.plan_id } });
};

const viewDetail = async (id) => {
  try {
    const res = await getTestPlanDetail(id);
    if (res.code === 200) {
      currentExecution.value = res.data;
      executionDialogVisible.value = true;
    }
  } catch (error) {
    message.error('获取详情失败：' + error.message);
  }
};

const cancelPlan = async (id) => {
  try {
    await cancelExecution(id);
    message.success('已取消执行');
    fetchTestPlans();
  } catch (error) {
    message.error('取消失败：' + error.message);
  }
};

const deletePlan = async (id) => {
  try {
    await deletePlanAPI(id);
    message.success('删除成功');
    fetchTestPlans();
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

const viewResultsFromDetail = () => {
  executionDialogVisible.value = false;
  router.push({ path: '/eval-v2/results', query: { test_plan_id: currentExecution.value.id } });
};

const triggerAutoScore = async () => {
  try {
    await batchAutoScore(currentExecution.value.id);
    message.success('自动评分已触发');
  } catch (error) {
    message.error('评分失败：' + error.message);
  }
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchTestPlans();
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'default',
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    pending: '待执行',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  };
  return texts[status] || status;
};

onMounted(() => {
  fetchTestPlans();
});

// 组件卸载时清理
onUnmounted(() => {
  stopPolling();
});
</script>

<style scoped>
.test-plan-list {
  padding: 24px;
}

.execution-progress {
  padding: 16px;
}

.progress-text {
  text-align: center;
  margin-top: 8px;
  color: #666;
}
</style>
